import { captureException } from "@sentry/react";
import { AlertCircle } from "lucide-react";
import { PreScreeningQuestion, QuestionType } from "../../types/landingPage";
import { getBMIStatusMessage } from "../../utils/bmiCalculator";
import { track } from "../../utils/rudderstack";
import { AIMedicationEntry } from "./AIMedicationEntry";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";

interface QuestionRendererProps {
  question: PreScreeningQuestion;
  value: string | string[];
  allAnswers?: Record<string, string | string[]>;
  bmi: number | null;
  onAnswer: (
    questionId: string,
    value: string | string[],
    disqualificationInfo?: {
      evaluatedDisqualify: boolean;
      disqualificationMessage?: string;
    }
  ) => void;
  onNext: () => void;
  onBack: () => void;
  onClearAnswers: () => void;
  error?: string;
  currentStep: number;
  totalSteps: number;
  validateOnBlur?: (questionId: string, value: string) => void;
  /**
   * When true, checks existing grid answers for disqualification when submit button is clicked.
   * This prevents users from bypassing disqualification by navigating back and re-submitting.
   * Defaults to true.
   */
  checkExistingAnswersOnSubmit?: boolean;
  // Tracking context properties
  landingPageId?: string;
  variantId?: string;
  masterId?: string;
  prescreenerId?: string;
}

export function QuestionRenderer({
  question,
  value,
  allAnswers,
  bmi,
  onAnswer,
  onNext,
  onBack,
  onClearAnswers,
  error,
  currentStep,
  totalSteps,
  validateOnBlur,
  landingPageId,
  variantId,
  masterId,
  prescreenerId,
}: QuestionRendererProps) {
  // Feature flag for AI medication entry
  const isAIEnabled = useFeatureFlag('aiMedicationEntry', false) || import.meta.env.DEV;
  
  // Helper function to render the question header
  const renderQuestionHeader = () => (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 mb-1" dangerouslySetInnerHTML={{ __html: question.question }}>
      </h3>
      {question.helpText && (
        <p className="text-sm text-gray-500">{question.helpText}</p>
      )}
    </div>
  );

  // Helper function to render back button and clear answers
  const renderBackButton = () => (
    <div className="mt-4 flex justify-between items-center">
      {currentStep > 0 && (
        <button
          type="button"
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 text-sm font-medium focus:outline-none focus:underline"
        >
          ← Back
        </button>
      )}
      <button
        type="button"
        onClick={onClearAnswers}
        className="text-gray-500 hover:text-gray-700 text-sm font-medium focus:outline-none focus:underline"
      >
        Clear all answers
      </button>
    </div>
  );

  // Helper function to render continue button
  const renderContinueButton = () => (
    <div className="mt-6">
      <button
        type="button"
        onClick={onNext}
        className="w-full px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        Continue
      </button>
    </div>
  );

  // Helper function to render error message
  const renderError = () =>
    error && (
      <div className="text-red-500 text-sm mt-1 flex items-center">
        <AlertCircle className="w-4 h-4 mr-1" />
        {error}
      </div>
    );

  // Helper function to track input interactions
  const trackInputInteraction = (interactionType: string) => {
    track("lp.pre_screener.input_interaction", {
      question_id: question.id,
      question_num: currentStep + 1,
      total_questions: totalSteps,
      question_type: question.type,
      interaction_type: interactionType,
    });
  };

  // Helper function to check if a cell option disqualifies
  const checkCellDisqualification = (
    cellOption:
      | {
          value: string;
          disqualify?:
            | boolean
            | ((answers: Record<string, any>, value: string) => boolean);
        }
      | undefined,
    answersForCheck: Record<string, any>
  ): boolean => {
    if (!cellOption || cellOption.disqualify === undefined) {
      return false;
    }

    if (typeof cellOption.disqualify === "function") {
      return cellOption.disqualify(answersForCheck, cellOption.value);
    }

    return cellOption.disqualify;
  };

  // Render different question types
  const renderQuestionContent = () => {
    switch (question.type) {
      case QuestionType.RADIO:
        return (
          <div>
            <div className="grid grid-cols-1 gap-3">
              {question.options?.map((option) => {
                const label =
                  option.dynamicLabel && allAnswers
                    ? option.dynamicLabel(allAnswers)
                    : option.label;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onAnswer(question.id, option.value, undefined);
                    }}
                    className={`w-full py-3 px-4 text-left rounded-lg border-2 relative pl-12 transition-all duration-200 ${
                      value === option.value
                        ? "bg-primary-50 border-primary-400 text-primary-700 shadow-sm"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm"
                    }`}
                  >
                    <div
                      className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${
                      value === option.value
                        ? "border-primary-500"
                        : "border-gray-300"
                    }`}
                    >
                      <div
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-200 
                      ${
                        value === option.value
                          ? "bg-primary-500 scale-100"
                          : "bg-transparent scale-0"
                      }`}
                      />
                    </div>
                    {label}
                  </button>
                );
              })}
            </div>
            {renderBackButton()}
          </div>
        );

      case QuestionType.RADIO_GRID:
        if (!question.gridLayout) return null;
        const { columnHeaders, rows } = question.gridLayout;

        let gridAnswers: Record<string, string> = {};
        try {
          if (typeof value === "string" && value) {
            gridAnswers = JSON.parse(value);
          } else if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value)
          ) {
            gridAnswers = value as Record<string, string>;
          }
        } catch (e) {
          captureException(e);
        }

        const answersForCellCheck = { ...(allAnswers || {}), bmi: bmi };

        return (
          <div className="space-y-8">
            {rows.map((row, rowIndex) => (
              <div key={rowIndex} className="border rounded-lg p-4 bg-white">
                <div className="text-lg font-medium text-gray-900 mb-3">
                  {row.label}
                  {row.sublabel && (
                    <div className="text-sm font-normal text-gray-500 mt-1">
                      {row.sublabel}
                    </div>
                  )}
                </div>
                <div className="w-full">
                  <div
                    className={`grid gap-2 ${columnHeaders.length === 3 ? 'grid-cols-3' : columnHeaders.length === 4 ? 'grid-cols-2' : `grid-cols-2 sm:grid-cols-${columnHeaders.length}`}`}
                  >
                    {row.values.map((cellOption, colIndex) => (
                      <button
                        key={`${rowIndex}-${colIndex}`}
                        type="button"
                        onClick={() => {
                          const newGridAnswers = { ...gridAnswers };
                          newGridAnswers[row.label] = cellOption.value;
                          const newGridValue = JSON.stringify(newGridAnswers);

                          const shouldDisqualify = checkCellDisqualification(
                            cellOption,
                            answersForCellCheck
                          );

                          onAnswer(question.id, newGridValue, {
                            evaluatedDisqualify: shouldDisqualify,
                            disqualificationMessage: shouldDisqualify
                              ? question.disqualifyMessage
                              : undefined,
                          });
                        }}
                        className={`py-2 px-3 rounded-md text-sm font-medium transition-all duration-200 min-w-[90px] text-center
                          ${
                            gridAnswers[row.label] === cellOption.value
                              ? "bg-primary-100 border-2 border-primary-500 text-primary-700"
                              : "bg-gray-50 border-2 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300"
                          }`}
                      >
                        {columnHeaders[colIndex]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* Quick select button - always visible */}
            {question.quickSelect && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {
                    const newGridAnswers = { ...gridAnswers };
                    let hasDisqualifyingAnswer = false;

                    // Process each row in the grid
                    rows.forEach((row) => {
                      const existingAnswer = newGridAnswers[row.label];

                      if (existingAnswer) {
                        const cellOption = row.values.find(
                          (v) => v.value === existingAnswer
                        );
                        if (
                          checkCellDisqualification(
                            cellOption,
                            answersForCellCheck
                          )
                        ) {
                          hasDisqualifyingAnswer = true;
                        }
                      } else {
                        // Set default value for unanswered items
                        const defaultSuffix =
                          question.quickSelect?.defaultValueSuffix || "_no";
                        const defaultCellOption = row.values.find((v) =>
                          v.value.endsWith(defaultSuffix)
                        );

                        if (defaultCellOption) {
                          newGridAnswers[row.label] = defaultCellOption.value;
                          if (
                            checkCellDisqualification(
                              defaultCellOption,
                              answersForCellCheck
                            )
                          ) {
                            hasDisqualifyingAnswer = true;
                          }
                        }
                      }
                    });

                    // Submit the answers with disqualification info
                    onAnswer(question.id, JSON.stringify(newGridAnswers), {
                      evaluatedDisqualify: hasDisqualifyingAnswer,
                      disqualificationMessage: hasDisqualifyingAnswer
                        ? question.disqualifyMessage
                        : undefined,
                    });

                    // Only proceed to next question if not disqualified
                    if (!hasDisqualifyingAnswer) {
                      onNext();
                    }
                  }}
                  className="w-full px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <div>
                    <span className="text-sm">
                      {question.quickSelect?.submitButtonText || "Submit"}
                    </span>
                    {/* Subtext logic based on whether some answers in the grid already exist */}
                    {Object.keys(gridAnswers).filter(
                      (key) =>
                        gridAnswers[key] !== undefined &&
                        gridAnswers[key] !== null &&
                        gridAnswers[key] !== ""
                    ).length === 0
                      ? question.quickSelect.submitButtonSubtext?.allText && (
                          <span className="block text-xs mt-1 text-primary-100">
                            {question.quickSelect.submitButtonSubtext.allText}
                          </span>
                        )
                      : Object.keys(gridAnswers).filter(
                          (key) =>
                            gridAnswers[key] !== undefined &&
                            gridAnswers[key] !== null &&
                            gridAnswers[key] !== ""
                        ).length < rows.length &&
                        question.quickSelect.submitButtonSubtext
                          ?.remainingText && (
                          <span className="block text-xs mt-1 text-primary-100">
                            {
                              question.quickSelect.submitButtonSubtext
                                .remainingText
                            }
                          </span>
                        )}
                  </div>
                </button>
              </div>
            )}
            {/* Ensure Continue button is not rendered if QuickSelect is present, or handle its display conditionally */}
            {!question.quickSelect && renderContinueButton()}
            {renderBackButton()}
          </div>
        );

      case QuestionType.CHECKBOX:
        return (
          <div>
            <div className="grid grid-cols-1 gap-3">
              {question.options?.map((option) => {
                const isChecked = Array.isArray(value) && value.includes(option.value);
                
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      const currentValues = Array.isArray(value) ? value : [];
                      let newValues: string[];
                      
                      if (!isChecked) {
                        if (option.exclusive) {
                          // If exclusive option is checked, clear all others
                          newValues = [option.value];
                        } else {
                          // Remove any exclusive options when checking a regular option
                          const nonExclusiveValues = currentValues.filter(v => {
                            const opt = question.options?.find(o => o.value === v);
                            return !opt?.exclusive;
                          });
                          newValues = [...nonExclusiveValues, option.value];
                        }
                      } else {
                        // Unchecking
                        newValues = currentValues.filter((v) => v !== option.value);
                      }
                      
                      // Check for disqualification
                      const hasDisqualifyingOption = newValues.some(val => {
                        const opt = question.options?.find(o => o.value === val);
                        if (typeof opt?.disqualify === 'function') {
                          return opt.disqualify(allAnswers || {}, newValues);
                        }
                        return opt?.disqualify === true;
                      });
                      
                      onAnswer(question.id, newValues, hasDisqualifyingOption ? {
                        evaluatedDisqualify: true,
                        disqualificationMessage: question.disqualifyMessage,
                      } : undefined);
                    }}
                    className={`w-full py-3 px-4 text-left rounded-lg border-2 relative pl-12 transition-all duration-200 ${
                      isChecked
                        ? "bg-primary-50 border-primary-400 text-primary-700 shadow-sm"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm"
                    }`}
                  >
                    <div
                      className={`absolute left-4 top-4 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200
                      ${
                        isChecked
                          ? "border-primary-500 bg-primary-500"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {isChecked && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7"></path>
                        </svg>
                      )}
                    </div>
                    <span>{option.label}</span>
                    {option.sublabel && (
                      <span className="block text-sm font-normal text-gray-500 mt-0.5">
                        {option.sublabel}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {Array.isArray(value) && value.length > 0 && renderContinueButton()}
            {renderBackButton()}
          </div>
        );

      case QuestionType.SELECT:
        return (
          <div>
            <select
              id={question.id}
              name={question.id}
              value={value as string}
              onChange={(e) => {
                onAnswer(question.id, e.target.value);
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-3 pl-3 pr-10 text-base focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">
                {question.placeholder || "Select an option"}
              </option>
              {question.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Display selected height if this is the height question */}
            {question.id === "height" && value && question.options && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md text-center">
                <p className="text-base font-medium text-gray-700">
                  Selected height:{" "}
                  <span className="text-primary-600">
                    {question.options.find((opt) => opt.value === value)?.label}
                  </span>
                </p>
              </div>
            )}

            {value && value !== "" && renderContinueButton()}
            {renderBackButton()}
          </div>
        );

      case QuestionType.TEXT:
        // Use defaultValue if no current value exists
        const textValue = (value as string) || question.defaultValue || "";
        return (
          <div>
            <input
              type="text"
              id={question.id}
              name={question.id}
              value={textValue}
              onChange={(e) => {
                onAnswer(question.id, e.target.value);
              }}
              onFocus={() => trackInputInteraction("text_input_focused")}
              onBlur={() => trackInputInteraction("text_input_blurred")}
              placeholder={question.placeholder}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            {textValue && textValue !== "" && renderContinueButton()}
            {renderBackButton()}
          </div>
        );

      case QuestionType.NUMBER:
        // Special case for weight question
        if (question.id === "weight") {
          return (
            <div>
              <div className="relative">
                <input
                  type="number"
                  id={question.id}
                  name={question.id}
                  value={value as string}
                  onChange={(e) => {
                    onAnswer(question.id, e.target.value);
                    // Calculate BMI on change as well
                    if (e.target.value) {
                      validateOnBlur?.(question.id, e.target.value);
                    }
                  }}
                  onFocus={() => trackInputInteraction("number_input_focused")}
                  onBlur={(e) => {
                    trackInputInteraction("number_input_blurred");
                    if (e.target.value) {
                      validateOnBlur?.(question.id, e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && value && value !== "") {
                      trackInputInteraction("number_input_enter_pressed");
                      e.preventDefault();
                      onNext();
                    }
                  }}
                  placeholder={question.placeholder}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-5 px-4 text-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  aria-label={question.question}
                  min="50"
                  max="500"
                />
                {question.suffix && (
                  <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-medium">
                    {question.suffix}
                  </span>
                )}
              </div>

              {/* BMI Display */}
              {question.id === "weight" && bmi !== null && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md shadow-sm">
                  <p className="text-center text-xl font-semibold text-gray-900">
                    Your BMI: {bmi.toFixed(1)}
                  </p>

                  {(question.minBMI !== undefined ||
                    question.maxBMI !== undefined) && (
                    <p className="text-center text-sm mt-2">
                      {(() => {
                        const resolvedMinBMI = typeof question.minBMI === "function"
                          ? question.minBMI(allAnswers ?? {})
                          : question.minBMI;
                        const bmiStatus = getBMIStatusMessage(
                          bmi,
                          resolvedMinBMI,
                          question.maxBMI
                        );
                        return (
                          <span
                            className={
                              bmiStatus.isValid
                                ? "text-green-600 font-medium"
                                : "text-yellow-600 font-medium"
                            }
                          >
                            {bmiStatus.text}
                          </span>
                        );
                      })()}
                    </p>
                  )}
                </div>
              )}
              {renderError()}
              {value && value !== "" && (
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={onNext}
                    className="w-full px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Continue
                  </button>
                </div>
              )}
              {renderBackButton()}
            </div>
          );
        }

        // Regular number input
        return (
          <div>
            <div className="relative">
              <input
                type="number"
                id={question.id}
                name={question.id}
                value={value as string}
                onChange={(e) => {
                  onAnswer(question.id, e.target.value);
                  // Calculate BMI on change as well
                  if (e.target.value) {
                    validateOnBlur?.(question.id, e.target.value);
                  }
                }}
                onFocus={() => trackInputInteraction("number_input_focused")}
                onBlur={(e) => {
                  trackInputInteraction("number_input_blurred");
                  if (e.target.value) {
                    validateOnBlur?.(question.id, e.target.value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && value && value !== "") {
                    trackInputInteraction("number_input_enter_pressed");
                    e.preventDefault();
                    onNext();
                  }
                }}
                placeholder={question.placeholder}
                inputMode="numeric"
                pattern="[0-9]*"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-5 px-4 text-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                aria-label={question.question}
              />
              {question.suffix && (
                <span className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-medium">
                  {question.suffix}
                </span>
              )}
            </div>
            {value && value !== "" && renderContinueButton()}
            {renderBackButton()}
          </div>
        );

      case QuestionType.AI_MEDICATION_ENTRY:
        // If AI is disabled and grid layout exists, render as radio grid
        if (!isAIEnabled && question.gridLayout) {
          const { columnHeaders, rows } = question.gridLayout;
          // Copy the radio grid rendering logic
          let gridAnswers: Record<string, string> = {};
          try {
            if (typeof value === "string" && value) {
              gridAnswers = JSON.parse(value);
            } else if (
              value &&
              typeof value === "object" &&
              !Array.isArray(value)
            ) {
              gridAnswers = value as Record<string, string>;
            }
          } catch (e) {
            captureException(e);
          }

          const answersForCellCheck = { ...(allAnswers || {}), bmi: bmi };

          return (
            <div className="space-y-4">
              {renderQuestionHeader()}
              <div className="space-y-8">
              {rows.map((row, rowIndex) => (
                <div key={rowIndex} className="border rounded-lg p-4 bg-white">
                  <div className="text-lg font-medium text-gray-900 mb-3">
                    {row.label}
                    {row.sublabel && (
                      <div className="text-sm font-normal text-gray-500 mt-1">
                        {row.sublabel}
                      </div>
                    )}
                  </div>
                  <div className="w-full">
                    <div
                      className={`grid gap-2 ${columnHeaders.length === 3 ? 'grid-cols-3' : columnHeaders.length === 4 ? 'grid-cols-2' : `grid-cols-2 sm:grid-cols-${columnHeaders.length}`}`}
                    >
                      {row.values.map((cellOption, colIndex) => (
                        <button
                          key={`${rowIndex}-${colIndex}`}
                          type="button"
                          onClick={() => {
                            const newGridAnswers = { ...gridAnswers };
                            newGridAnswers[row.label] = cellOption.value;
                            const newGridValue = JSON.stringify(newGridAnswers);

                            const shouldDisqualify = checkCellDisqualification(
                              cellOption,
                              answersForCellCheck
                            );

                            onAnswer(question.id, newGridValue, {
                              evaluatedDisqualify: shouldDisqualify,
                              disqualificationMessage: shouldDisqualify
                                ? question.disqualifyMessage
                                : undefined,
                            });
                          }}
                          className={`py-2 px-3 rounded-md text-sm font-medium transition-all duration-200 min-w-[90px] text-center
                          ${
                            gridAnswers[row.label] === cellOption.value
                              ? "bg-primary-100 border-2 border-primary-400 text-primary-700"
                              : "bg-gray-50 border-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400"
                          }
                          ${
                            cellOption.disqualify
                              ? "hover:border-red-300"
                              : ""
                          }`}
                        >
                          {columnHeaders[colIndex]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {renderError()}
              {renderBackButton()}
              </div>
            </div>
          );
        }
        
        return (
          <AIMedicationEntry
            question={question}
            value={value}
            onAnswer={onAnswer}
            onDisqualify={(message) => {
              // Handle disqualification
              onAnswer(question.id, value, {
                evaluatedDisqualify: true,
                disqualificationMessage: message,
              });
            }}
            allowBack={question.allowBack}
            onBack={onBack}
            onNext={onNext}
            landingPageId={landingPageId}
            variantId={variantId}
            masterId={masterId}
            prescreenerId={prescreenerId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 pb-12 pt-2">
      {question.type !== QuestionType.AI_MEDICATION_ENTRY && renderQuestionHeader()}
      {renderQuestionContent()}
    </div>
  );
}